import { type StateValue, interpret } from 'xstate';
import { toStatePaths } from 'xstate/lib/utils';
import { getLogger } from '../../libs/logger';
import { callStateMachine } from './callStateMachine';
import { CallState } from './call.types';

/**
 * Convert state value to string dot notation, useful for log readability.
 */
const toStatePath = (value: StateValue): string =>
  toStatePaths(value).flat().join('.');

/**
 * The call state machine is an application singleton.
 * Send 'START' to start the call-init process.
 */
export const callActor = interpret(callStateMachine);

callActor.start();

/**
 * Reads the singleton call state machine outside of React.
 *
 * @returns `true` when a call is being set up, ringing, in progress, on hold,
 * or tearing down — i.e. any state other than `idle`. Lets non-React code (such
 * as the chunk-reload recovery) avoid disrupting a live call with a full-page
 * reload. Mirrors how `useCallState` derives the active state name from the
 * machine value (string for top-level states, first key for nested states).
 */
export const getIsCallActive = (): boolean => {
  const { value } = callActor.getSnapshot();
  const stateName = typeof value === 'string' ? value : Object.keys(value)[0];
  return stateName !== CallState.Idle;
};

/**
 * Log transitions and state changes.
 */
callActor.onTransition((state) => {
  const { event, value, history } = state;
  const transition = event.type;
  const currentState = toStatePath(value);
  const prevState = history ? toStatePath(history.value) : 'null';

  if (prevState !== 'null') {
    const logger = getLogger('callStateMachine');
    logger.info(
      `[callStateMachine/transition] ${transition} : ${prevState} -> ${currentState}`,
      { transition, state: currentState, prevState },
    );
  }
});
